import React, { useRef, useEffect, useState } from 'react';
import axios from "axios";

function NewsSection() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const scrollRef = useRef(null);
    const [scrollBarWidth, setScrollBarWidth] = useState(0);
    const [scrollBarLeft, setScrollBarLeft] = useState(0);
    const [posts, setPosts] = useState([]);

    // IntersectionObserverでフェードイン
    useEffect(() => {
        let hasAppeared = false;
        const observer = new window.IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (!hasAppeared) {
                        setShouldAnimate(true);
                        hasAppeared = true;
                    }
                    setVisible(true);
                } else {
                    setVisible(false);
                    hasAppeared = false;
                }
            },
            { threshold: 0.2 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    // WordPressから投稿データを取得
    useEffect(() => {
        const fetchPosts = async () => {
        try {
            const postsResponse = await axios.get(
                "https://chaln.co.jp/wp-json/wp/v2/posts?per_page=10",
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    timeout: 10000, // 10秒のタイムアウト
                }
            );
            
            console.log('Posts Response:', postsResponse.data); // デバッグ用
            setPosts(postsResponse.data);
        } catch (error) {
            console.error("Error fetching posts:", error);
            console.error("Error details:", error.response?.data);
            
            // エラー時はサンプルデータを使用
            setPosts([
                {
                    id: 1,
                    title: { rendered: "採用情報を更新しました" },
                    date: "2025-07-01T00:00:00",
                    featured_media_url: `${process.env.PUBLIC_URL}/image/採用.png`,
                    excerpt: { rendered: "新しい職種の募集を開始しました。" }
                },
                {
                    id: 2,
                    title: { rendered: "サービス情報を更新しました" },
                    date: "2025-06-15T00:00:00",
                    featured_media_url: `${process.env.PUBLIC_URL}/image/placeholder.jpg`,
                    excerpt: { rendered: "新しいサービスを追加しました。" }
                }
            ]);
        }
    };

    fetchPosts();
    }, []);

    // 日付フォーマット関数
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString("ja-JP", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).replace(/\//g, '/');
    };

    // HTMLタグを除去する関数
    const stripHtml = (html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || "";
    };

    // スクロールバーの幅・位置を計算
    const updateScrollBar = () => {
        const el = scrollRef.current;
        if (!el) return;
        const ratio = el.offsetWidth / el.scrollWidth;
        setScrollBarWidth(el.offsetWidth * ratio);
        setScrollBarLeft((el.scrollLeft / el.scrollWidth) * el.offsetWidth);
    };

    useEffect(() => {
        updateScrollBar();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateScrollBar);
        window.addEventListener('resize', updateScrollBar);
        return () => {
            el.removeEventListener('scroll', updateScrollBar);
            window.removeEventListener('resize', updateScrollBar);
        };
    }, [posts]);

    // バーをドラッグでスクロール
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragStartScroll = useRef(0);

    const onBarMouseDown = (e) => {
        isDragging.current = true;
        dragStartX.current = e.clientX;
        dragStartScroll.current = scrollRef.current.scrollLeft;
        document.addEventListener('mousemove', onBarMouseMove);
        document.addEventListener('mouseup', onBarMouseUp);
    };

    const onBarMouseMove = (e) => {
        if (!isDragging.current) return;
        const el = scrollRef.current;
        const dx = e.clientX - dragStartX.current;
        const scrollable = el.scrollWidth - el.offsetWidth;
        const barTrackWidth = el.offsetWidth - scrollBarWidth;
        if (barTrackWidth > 0) {
            el.scrollLeft = dragStartScroll.current + (dx * scrollable / barTrackWidth);
        }
    };

    const onBarMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', onBarMouseMove);
        document.removeEventListener('mouseup', onBarMouseUp);
    };

    return (
        <section
            id="news"
            ref={ref}
            className={
                ` mb-16 pt-16 pb-16 max-w-5xl mx-auto  ${visible ? "opacity-100" : "opacity-0"
                }`}
            style={{
                willChange: 'opacity',
                transition: shouldAnimate
                    ? 'opacity 1.2s cubic-bezier(.4,0,.2,1)'
                    : 'none'
            }}
            onTransitionEnd={() => setShouldAnimate(false)}
        >
            <div
                ref={scrollRef}
                className="flex gap-8 overflow-x-auto scrollbar-hide py-4 w-full"
                style={{
                    scrollBehavior: 'smooth',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {posts.map((post) => (
                    <div
                        key={post.id}
                        className="w-[270px] md:w-[340px] bg-black/50 rounded-2xl p-6 shadow-2xl flex-shrink-0 transition hover:scale-105"
                    >
                        <img
                            src={post.featured_media_url || `${process.env.PUBLIC_URL}/image/placeholder.jpg`}
                            alt={stripHtml(post.title.rendered)}
                            className="w-full h-48 object-cover rounded-xl mb-4"
                            draggable={false}
                        />
                        <div className="text-sm text-white mb-2">{formatDate(post.date)}</div>
                        <div className="text-2xl font-bold text-orange-400 mb-2">
                            {stripHtml(post.title.rendered)}
                        </div>
                        <div className="text-base text-white">
                            {stripHtml(post.excerpt?.rendered || "")}
                        </div>
                    </div>
                ))}
            </div>
            {/* カスタムスクロールバー */}
            <div className="relative h-4 mt-2 mb-2 select-none">
                <div
                    className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-white via-blue-500/30 to-purple-500/30"
                />
                <div
                    className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-gray-800 shadow-lg cursor-pointer transition"
                    style={{
                        left: `${scrollBarLeft}px`,
                        width: `${scrollBarWidth}px`,
                        minWidth: '40px',
                        maxWidth: '100%',
                        opacity: 0.9,
                    }}
                    onMouseDown={onBarMouseDown}
                />
            </div>

            {/* スクロールバー非表示用 */}
            <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
        </section>
    );
}

export default NewsSection;